import { useCallback, useEffect, useRef, useState } from "react";
import { onSnapshot, orderBy, query } from "firebase/firestore";
import { signalsCollection, writeSignal } from "../firebase";

const ICE_SERVERS = [{ urls: "stun:stun.l.google.com:19302" }];

const useRoomWebRTC = ({
  roomId,
  participantId,
  localStream,
  shouldInitiate,
  onError,
}) => {
  const pcRef = useRef(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [connectionState, setConnectionState] = useState("new");
  const [iceConnectionState, setIceConnectionState] = useState("new");
  const hasOfferedRef = useRef(false);
  const hasReceivedSignalRef = useRef(false);

  const ensurePeerConnection = useCallback(() => {
    if (!roomId) return null;
    if (pcRef.current) return pcRef.current;
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pc.onicecandidate = (event) => {
      if (event.candidate && roomId) {
        writeSignal({
          roomId,
          payload: {
            type: "ice",
            from: participantId,
            candidate: event.candidate.toJSON(),
          },
        }).catch((error) => console.error("ICE send error", error));
      }
    };

    pc.ontrack = (event) => {
      const [stream] = event.streams;
      setRemoteStream(stream);
    };

    pc.onconnectionstatechange = () => {
      setConnectionState(pc.connectionState);
    };

    pc.oniceconnectionstatechange = () => {
      setIceConnectionState(pc.iceConnectionState);
      // If ICE fails but we haven't received any peer signals, user is likely alone
      if (pc.iceConnectionState === "failed" && !hasReceivedSignalRef.current) {
        console.info("[WebRTC] Connection failed - likely no peer in room");
      } else if (pc.iceConnectionState === "failed") {
        console.error("[WebRTC] Connection failed with peer present");
        onError?.(new Error("Connection to partner failed"));
      }
    };

    pcRef.current = pc;
    return pc;
  }, [participantId, roomId]);

  useEffect(() => {
    const pc = ensurePeerConnection();
    if (!pc || !localStream) return undefined;
    localStream.getTracks().forEach((track) => {
      pc.addTrack(track, localStream);
    });
    return undefined;
  }, [ensurePeerConnection, localStream]);

  useEffect(() => {
    if (!roomId || !participantId) return undefined;
    const signalsRef = query(
      signalsCollection(roomId),
      orderBy("createdAt", "asc")
    );

    const unsubscribe = onSnapshot(
      signalsRef,
      async (snapshot) => {
        const pc = ensurePeerConnection();
        if (!pc) return;
        for (const change of snapshot.docChanges()) {
          if (change.type !== "added") continue;
          const data = change.doc.data();
          if (!data || data.from === participantId) continue;

          try {
            // Mark that we've received a signal from a peer
            hasReceivedSignalRef.current = true;

            if (data.type === "offer" && data.sdp) {
              console.info("[WebRTC] Received offer from peer");
              await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
              const answer = await pc.createAnswer();
              await pc.setLocalDescription(answer);
              await writeSignal({
                roomId,
                payload: {
                  type: "answer",
                  from: participantId,
                  sdp: answer,
                },
              });
            } else if (data.type === "answer" && data.sdp) {
              console.info("[WebRTC] Received answer from peer");
              if (
                pc.signalingState === "have-local-offer" ||
                pc.signalingState === "have-remote-offer"
              ) {
                await pc.setRemoteDescription(
                  new RTCSessionDescription(data.sdp)
                );
              }
            } else if (data.type === "ice" && data.candidate) {
              await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
            }
          } catch (err) {
            console.error("WebRTC signal handling error", err);
            onError?.(err);
          }
        }
      },
      (err) => {
        console.error("Signal listener error", err);
        onError?.(err);
      }
    );

    return unsubscribe;
  }, [ensurePeerConnection, onError, participantId, roomId]);

  useEffect(() => {
    if (!shouldInitiate || !localStream || hasOfferedRef.current) return;

    const startOffer = async () => {
      try {
        const pc = ensurePeerConnection();
        if (!pc) return;
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await writeSignal({
          roomId,
          payload: { type: "offer", from: participantId, sdp: offer },
        });
        hasOfferedRef.current = true;
      } catch (err) {
        console.error("Offer creation error", err);
        onError?.(err);
      }
    };

    startOffer();
  }, [ensurePeerConnection, localStream, onError, participantId, roomId, shouldInitiate]);

  useEffect(() => {
    return () => {
      pcRef.current?.getSenders().forEach((sender) => sender.track?.stop());
      pcRef.current?.close();
      pcRef.current = null;
    };
  }, []);

  return { remoteStream, connectionState, iceConnectionState };
};

export default useRoomWebRTC;

